import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { CAJUSPLogo } from "../components/CAJUSPLogo";
import { NAV_LINKS, WHATSAPP_URL } from "../data/content";
import { scrollToId } from "../lib/scroll";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (e, href) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(href);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                    scrolled
                        ? "border-b border-cream-line bg-cream/90 shadow-[0_8px_30px_rgba(18,21,25,0.06)] backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[88px] max-w-7xl items-center justify-between px-5 sm:px-8">
                    <a
                        href="#inicio"
                        data-testid="header-logo-link"
                        onClick={(e) => handleNav(e, "#inicio")}
                        aria-label="CAJUSP — voltar ao início"
                    >
                        <CAJUSPLogo
                            compact
                            variant={scrolled ? "dark" : "light"}
                            className="h-11 w-auto sm:h-12"
                        />
                    </a>
                    <nav className="hidden items-center gap-5 lg:flex xl:gap-7" aria-label="Navegação principal">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.id}
                                href={l.href}
                                data-testid={`nav-link-${l.id}`}
                                onClick={(e) => handleNav(e, l.href)}
                                className={`group relative whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.13em] transition-colors ${
                                    scrolled
                                        ? "text-graphite-text hover:text-ink"
                                        : "text-graphite-mute hover:text-cream"
                                }`}
                            >
                                {l.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
                            </a>
                        ))}
                    </nav>
                    <div
                        className={`flex items-center gap-3 sm:ml-4 sm:border-l sm:pl-6 ${
                            scrolled ? "sm:border-cream-line" : "sm:border-graphite-line"
                        }`}
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-whatsapp-button"
                            className="hidden items-center gap-2 whitespace-nowrap rounded-sm bg-gold px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-graphite-deep transition-colors hover:bg-gold-light sm:inline-flex"
                        >
                            <MessageCircle size={14} strokeWidth={2.2} />
                            Fale conosco
                        </a>
                        <button
                            type="button"
                            data-testid="mobile-menu-button"
                            onClick={() => setOpen((v) => !v)}
                            aria-label={open ? "Fechar menu" : "Abrir menu"}
                            className={`inline-flex h-10 w-10 items-center justify-center rounded-sm border lg:hidden ${
                                scrolled
                                    ? "border-cream-line text-ink"
                                    : "border-graphite-line text-cream"
                            }`}
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 bg-graphite-deep/[0.98] backdrop-blur-sm lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-full flex-col justify-center gap-1 px-8">
                            {NAV_LINKS.map((l, i) => (
                                <motion.a
                                    key={l.id}
                                    href={l.href}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    onClick={(e) => handleNav(e, l.href)}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.45 }}
                                    className="border-b border-graphite-line py-4 font-serif text-3xl text-cream transition-colors hover:text-gold-light"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                            <motion.a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-whatsapp-button"
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4, duration: 0.45 }}
                                className="mt-8 inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-graphite-deep"
                            >
                                <MessageCircle size={16} />
                                Fale conosco
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
